import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import { tuglaTurleri } from '@mocks/materials/tugla';
import { mockCompanies } from '@mocks/firmalar';
import mockBlocks, { getBlocksByConstructionId } from '@mocks/bloklar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';
import KDVSummary from '../../components/KDVSummary';

/**
 * Props:
 *   onSubmit(data, isEdit) — Normal malzeme sayfasından kullanım
 *   onMaterialSubmit(enrichedData) — Finance modundan açıldığında
 *   submitLabel — Buton yazısı
 */
export default function TuglaModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false, insaatId }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );

    let availableBlocks = [];
    if (insaatId) {
        availableBlocks = getBlocksByConstructionId(insaatId).map(b => ({ id: b.id, name: b.name, constructionId: b.constructionId }));
    } else {
        availableBlocks = mockBlocks.map(b => ({ id: b.id, name: `${b.constructionName} - ${b.name}`, realName: b.name, constructionId: b.constructionId }));
    }

    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: initialData || {
            siparisTarihi: new Date().toISOString().split('T')[0],
            tur: '13.5 Yatay Delikli',
            kdvOrani: DEFAULT_KDV_RATE,
        },
    });

    const watchedBirimFiyat = watch('birimFiyat');
    const watchedMiktar = watch('miktar');
    const watchedKdvOrani = watch('kdvOrani');

    const handleFormSubmit = (data) => {
        const result = calculateKDV({ unitPrice: data.birimFiyat, quantity: data.miktar, vatRate: data.kdvOrani });
        data.toplamFiyat = result.toplamHaric;
        data.kdvTutari = result.kdvTutari;
        data.kdvDahilToplam = result.toplamDahil;
        data.tedarikci = selectedCompany?.name || data.tedarikci;
        data.supplierId = selectedCompany?.id || null;

        if (!insaatId) {
            const selectedBlock = availableBlocks.find(b => b.name === data.kullanimAlani || b.realName === data.kullanimAlani);
            if (selectedBlock) { data.insaatId = selectedBlock.constructionId; data.kullanimAlani = selectedBlock.realName || selectedBlock.name; }
        } else { data.insaatId = insaatId; }

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.kdvDahilToplam,
            autoDescription: `Tuğla Alımı — ${data.miktar} adet ${data.tur}`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const handleClose = () => { reset(); onClose(); };
    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors";
    const errorClass = "border-error focus:ring-error/50";

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Tuğla Siparişi Düzenle' : 'Yeni Tuğla Siparişi'} size="lg"
            footer={<><Button variant="ghost" onClick={handleClose} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading}>{submitLabel || (isEdit ? 'Güncelle' : 'Sipariş Oluştur')}</Button></>}>
            <form className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <CompanySelector
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                            companies={mockCompanies}
                            label="Tedarikçi / Firma"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi <span className="text-error">*</span></label>
                        <input type="date" {...register('siparisTarihi', { required: true })} className={`${inputClass} ${errors.siparisTarihi ? errorClass : ''}`} />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Kullanım Alanı</label>
                        <select {...register('kullanimAlani')} className={inputClass}>
                            <option value="">Seçiniz</option>
                            {availableBlocks.map(b => (<option key={b.id} value={b.realName || b.name}>{b.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Tuğla Türü <span className="text-error">*</span></label>
                        <select {...register('tur', { required: true })} className={inputClass}>
                            {tuglaTurleri.map(s => (<option key={s.key} value={s.key}>{s.label} - {s.aciklama}</option>))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Miktar (adet) <span className="text-error">*</span></label>
                        <input type="number" {...register('miktar', { required: true, valueAsNumber: true })} placeholder="0" className={`${inputClass} ${errors.miktar ? errorClass : ''}`} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Birim Fiyat (₺)</label>
                        <input type="number" step="0.5" {...register('birimFiyat', { valueAsNumber: true })} placeholder="8.5" className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">KDV Oranı (%)</label>
                        <input type="number" step="1" {...register('kdvOrani', { valueAsNumber: true })} placeholder="20" className={inputClass} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1.5">Durum</label>
                        <select {...register('durum')} className={inputClass}>
                            <option value="bekliyor">Bekliyor</option>
                            <option value="teslim">Teslim Edildi</option>
                        </select>
                    </div>
                </div>
                <KDVSummary unitPrice={watchedBirimFiyat} quantity={watchedMiktar} vatRate={watchedKdvOrani} />
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1.5">Not</label>
                    <textarea {...register('not')} rows={2} placeholder="Sipariş notu..." className={`${inputClass} resize-none`} />
                </div>
            </form>
        </Modal>
    );
}
